import React from "react";
import { Box, LinearProgress } from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { getProgressBarColors } from "../utils/progressCalculations";
import { ProgressWrapper } from "./ProgressWrapper";

dayjs.extend(customParseFormat);

interface TimeProgressBarProps {
  initialDate: string;
  endDate: string;
  status: string;
}

// Бэк (mapKeywordOrderDetails) отдаёт даты в формате DATE_TIME_EU
const DATE_TIME_EU = "DD.MM.YYYY HH:mm";

// Fallback-данные (createFallbackData) приходят как ISO-строки
const parseOrderDate = (value: string) => {
  const parsed = dayjs(value, DATE_TIME_EU, true);
  return parsed.isValid() ? parsed : dayjs(value);
};

const formatShortDate = (value: string): string => {
  const parsed = parseOrderDate(value);
  return parsed.isValid() ? parsed.format("DD.MM") : "—";
};

const FINAL_STATUSES = ["DONE", "COMPLETED", "PAUSED", "CANCELED"];

const resolveStatus = (status: string, progress: number): string => {
  const upperStatus = status?.toUpperCase() || "";
  if (FINAL_STATUSES.includes(upperStatus)) return upperStatus;
  if (progress === 100) return "DONE";
  return upperStatus || "IN_PROGRESS";
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const calculateProgressFromDates_old = (
  initialDate: string,
  endDate: string,
): number => {
  const start = dayjs(initialDate, "YYYY-MM-DD HH:mm");
  const end = dayjs(endDate, "YYYY-MM-DD HH:mm");
  const now = dayjs();

  if (now.isBefore(start)) {
    return 0;
  }

  if (now.isAfter(end)) {
    return 100;
  }

  const totalDuration = end.diff(start);
  const elapsedDuration = now.diff(start);
  const progress = (elapsedDuration / totalDuration) * 100;
  console.log("progress", progress);
  return Math.floor(Math.max(0, Math.min(100, progress)));
};

const calculateProgressFromDates = (
  initialDate: string,
  endDate: string,
): number => {
  const start = parseOrderDate(initialDate);
  const end = parseOrderDate(endDate);
  const now = dayjs();

  if (!start.isValid() || !end.isValid()) {
    return 0;
  }

  if (now.isBefore(start)) {
    return 0;
  }

  if (now.isAfter(end)) {
    return 100;
  }

  const totalDuration = end.diff(start);
  if (totalDuration <= 0) {
    return 100;
  }

  const elapsedDuration = now.diff(start);
  const progress = (elapsedDuration / totalDuration) * 100;
  return Math.floor(Math.max(0, Math.min(100, progress)));
};

export const TimeProgressBar: React.FC<TimeProgressBarProps> = ({
  initialDate,
  endDate,
  status,
}) => {
  const calculatedProgress = calculateProgressFromDates(initialDate, endDate);
  const resolvedStatus = resolveStatus(status, calculatedProgress);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        width: "144px",
        height: "41px",
        margin: "24px auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          position: "relative",
        }}
      >
        <span style={{ fontSize: 12, color: "var(--ui-text-secondary-default)" }}>
          {formatShortDate(initialDate)}
        </span>
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                width: "4px",
                height: "4px",
                borderRadius: "50%",
                backgroundColor: "var(--ui-text-caption-default)",
              }}
            />
            <Box
              sx={{
                width: "8px",
                height: "1px",
                backgroundColor: "var(--ui-text-caption-default)",
              }}
            />
          </Box>
          <span
            style={{
              color: "var(--ui-text-caption-default)",
              fontSize: "10px",
              lineHeight: 1,
              margin: "0 2px",
            }}
          >
            →
          </span>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                width: "8px",
                height: "1px",
                backgroundColor: "var(--ui-text-caption-default)",
              }}
            />
            <Box
              sx={{
                width: "4px",
                height: "4px",
                borderRadius: "50%",
                backgroundColor: "var(--ui-text-caption-default)",
              }}
            />
          </Box>
        </Box>
        <span style={{ fontSize: 12, color: "var(--ui-text-secondary-default)" }}>
          {formatShortDate(endDate)}
        </span>
      </Box>
      <Box sx={{ position: "relative", height: "20px" }}>
        <ProgressWrapper
          value={calculatedProgress}
          status={resolvedStatus}
        />
      </Box>
    </Box>
  );
};
